import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NbDialogRef} from '@nebular/theme';
import {Column} from '../../../data-source/models/column.model';
import {UserOrGroup} from '../../models/userOrGroup.model';
import {UserInUserFilter} from '../../models/userInUserfilter.model';
import {UserFilterDetails} from '../../models/userFilterDetails.model';
import {AbstractFacade} from '../../models/abstract-facade';
import {fromEvent, Subscription} from 'rxjs';
import {UserFilter} from '../../models/user-filter.model';
import {debounceTime, distinctUntilChanged, filter, tap} from 'rxjs/operators';
import {DataSourceFacade} from "../../../data-source/facade/data-source.facade";

@Component({
  selector: 'morva-user-filter-action',
  templateUrl: './user-filter-action.component.html',
  styleUrls: ['./user-filter-action.component.scss'],
})
export class UserFilterActionComponent implements OnInit, OnDestroy,AfterViewInit {
  @ViewChild('searchColumnKey') searchColumnInput: ElementRef;
  @ViewChild('searchUserOrGroupKey') searchUserInput: ElementRef;
  @ViewChild('searchInUsersKey') searchInUsersInput: ElementRef;
  @ViewChild('searchColumnValueKey') searchColumnValueInput: ElementRef;
  actionType: 'Create' | 'Edit' | 'View';
  userFilterType: 'Dashboard' | 'Chart';
  columns: Column[];
  showColumns: Column[];
  userFilter: Partial<UserFilter>;
  userFilterDetails: Partial<UserFilterDetails>;
  usersAndGroups: UserOrGroup[] = [];
  showUsersAndGroups: UserOrGroup[] = [];
  headerText: string;
  userAndValueTitle: string;
  selectedColumn: Column;
  showSelectedColumnValues: {value: string; isChecked: boolean}[] = [];
  selectedColumnValues: string[] = [];
  selectedUsersOrGroupsInUserFilter: Partial<UserInUserFilter>[] = [];
  showSelectedUsersOrGroupsInUserFilter: Partial<UserInUserFilter>[] = [];
  selectedUserOrGroupInUserFilter: Partial<UserInUserFilter>;
  userFilterName: string;

  loadingColumnValues = false;
  loadingUserFilterDetails = false;
  isValidated = false;
  submitting = false;
  maxDataAppend = 100;
  rowNumber = 100;
  requestedForNewData = false;
  dataSourceId: string;
  searchValueKey: string;
  counterAppendData = 0;
  subscriptions: Subscription[] = [];
  showSelectedValues: string[];
  constructor(private userFilterAction: NbDialogRef<any>, public abstractFacade: DataSourceFacade) { }

  ngOnInit(): void {
    if(this.actionType === 'Create') {
      this.initializingForCreation();
    } else if (this.actionType === 'Edit') {
      this.initializingForEdit();
    } else if (this.actionType === 'View') {
      this.initializingForView();
    }
    this.setSubscribers();
  }

  ngAfterViewInit(): void {
    fromEvent(this.searchColumnValueInput.nativeElement,'keyup')
        .pipe(
            filter(Boolean),
            debounceTime(1000),
            distinctUntilChanged(),
            tap(() => {
              this.searchValueKey = this.searchColumnValueInput.nativeElement.value;
              this.searchColumnValue(this.searchValueKey);
            }),
        )
        .subscribe();
  }

  closeDialog(): void {
    this.resetFields();
    this.userFilterAction.close(true);
  }

  initializingForCreation(): void {
    this.showColumns = [...this.columns];
    this.abstractFacade.loadUserAndGroupToAddUserFilter(undefined);
    this.setSubscribersForDataSource();
  }

  initializingForEdit(): void {
    this.loadingUserFilterDetails = true;
    this.showColumns = [...this.columns];
    this.abstractFacade.loadUserAndGroupToAddUserFilter(this.userFilter.userFilterId);
    this.abstractFacade.loadUserFilterDetails(this.userFilter);
    this.abstractFacade.loadColumnValues(this.userFilter.columnId, [], {
      rowNumber: this.rowNumber,
      searchKey: null,
      needNewQuery: true,
    });
    this.selectedColumn = this.columns.find(column => column.id === this.userFilter.columnId);
    this.userFilterName = this.userFilter.name;
    this.setSubscribersForDataSource();
  }

  initializingForView(): void {
    this.loadingUserFilterDetails = true;
    this.abstractFacade.loadUserFilterDetailsInViewMode(this.userFilter, this.userFilterType);
    this.setSubscribersForDashboard();
  }

  resetFields(): void {
    this.searchColumnValueInput.nativeElement.value = '';
    this.selectedUserOrGroupInUserFilter = undefined;
    this.selectedColumn = undefined;
  }

  setSubscribersForDataSource(): void {
    this.subscriptions.push(this.abstractFacade
    .getDataSourceId$().subscribe(datasourceId => this.dataSourceId = datasourceId));

    this.subscriptions.push(this.abstractFacade.getUsersAndGroups$().subscribe((usersAndGroups) => {
      this.usersAndGroups = usersAndGroups;
      this.showUsersAndGroups = usersAndGroups;
    }));

    this.subscriptions.push(this.abstractFacade.getSelectedColumnValues$().subscribe((columnValues) => {
      this.selectedColumnValues = columnValues;
      this.updateSelectedValues(this.selectedUserOrGroupInUserFilter,this.selectedColumnValues);
      this.loadingColumnValues = false;
      this.requestedForNewData = false;
    }));

    this.subscriptions.push(this.abstractFacade.getUserFilterDetails$()
        .subscribe((userFilterDetails) => {
          this.userFilterDetails = userFilterDetails;
          this.loadingUserFilterDetails = false;
    }));
  }

  setSubscribersForDashboard(): void {
    this.subscriptions.push(this.abstractFacade.getUserFilterDetails$()
        .subscribe((userFilterDetails) => {
          this.userFilterDetails = userFilterDetails;
          if(userFilterDetails) {
            this.loadingUserFilterDetails = false;
          }
    }));
  }

  setSubscribers(): void {

    this.abstractFacade.getSelectedUsersInUserFilter$().subscribe(
        (users) => {
          this.selectedUsersOrGroupsInUserFilter = users;
          this.showSelectedUsersOrGroupsInUserFilter = [...users];
          this.validator();
        },
    );

    this.subscriptions.push(this.abstractFacade.getSubmitState$().subscribe(
        (submit) => {
          if(submit === 'submitted') {
            this.abstractFacade.setSubmitState('notSubmitted');
            this.closeDialog();
            this.submitting = false;
          } else if(submit === 'notSubmitted') {
            this.submitting = false;
          }
        },
    ));
  }

  changeSelectColumn(column: Column, index: number): void {
    this.selectedColumn = column;
    this.showColumns.splice(index,1);
    this.loadingColumnValues = true;
    this.validator();
    this.abstractFacade.loadColumnValues(column.id,[], {
      rowNumber: this.rowNumber,
      searchKey: null,
      needNewQuery: true,
    });
  }

  deleteColumn(): void {
    this.resetFields();
    this.validator();
    this.searchColumnInput.nativeElement.value = '';
    this.showColumns = [...this.columns];
    this.abstractFacade.resetSelectedUsersOrGroupsInUserFilter();
    this.abstractFacade.resetSelectedColumnValues();
  }

  searchColumn(searchKey: string): void {
    this.showColumns = [];
    this.columns.forEach((column) => {
      if(column.name.toLowerCase().includes(searchKey.toLowerCase())) {
        this.showColumns.push(column);
      }
    });
  }

  addUserOrGroupToUserFilter(userOrGroup: UserOrGroup): void {
    this.setSelectedUserOrGroup({userFilterRecipientId: userOrGroup.userFilterRecipientId,
      userFilterRecipientType: userOrGroup.userFilterRecipientType,
      recipientName: userOrGroup.recipientName,
      selectedValues: [],
      index: this.selectedUsersOrGroupsInUserFilter.length,
      isInclude: true});
    this.abstractFacade.addUserToUserFilter(userOrGroup);
    this.updateShowUsersOrGroups();
  }

  removeUserOrGroup(userInUserFilter: Partial<UserInUserFilter>): void {
    this.abstractFacade.removeUserOrGroup(userInUserFilter);
    this.showUsersAndGroups.push({
      userFilterRecipientType: userInUserFilter.userFilterRecipientType,
      userFilterRecipientId: userInUserFilter.userFilterRecipientId,
      recipientName: userInUserFilter.recipientName,
    });
    if(userInUserFilter.userFilterRecipientId === this.selectedUserOrGroupInUserFilter?.userFilterRecipientId) {
      this.selectedUserOrGroupInUserFilter = undefined;
      this.showSelectedColumnValues.forEach(tempValue => tempValue.isChecked = false);
    }
  }

  searchUserOrGroup(searchKey: string): void {
    this.showUsersAndGroups = [];
    this.usersAndGroups.forEach(tempItem => {
      if(tempItem.recipientName.toLowerCase().includes(searchKey.toLowerCase()) && !this.isExistInSelectionUsers(tempItem)) {
        this.showUsersAndGroups.push(tempItem);
      }
    });
  }

  updateShowUsersOrGroups(): void {
    this.showUsersAndGroups = [];
    this.searchUserInput.nativeElement.value = '';
    this.usersAndGroups.forEach(userOrGroup => {
      if(!this.isExistInSelectionUsers(userOrGroup)) {
        this.showUsersAndGroups.push(userOrGroup);
      }
    });
  }

  isExistInSelectionUsers(userOrGroup: UserOrGroup): boolean {
    const findIndex = this.selectedUsersOrGroupsInUserFilter
        .findIndex(tempSelected => tempSelected.userFilterRecipientId === userOrGroup.userFilterRecipientId);
    return findIndex > -1;
  }

  setSelectedUserOrGroup(item: Partial<UserInUserFilter>): void {
    this.selectedUserOrGroupInUserFilter = item;
    if(this.actionType !== 'View') {
      if(item) {
        this.updateSelectedValues(this.selectedUserOrGroupInUserFilter, this.selectedColumnValues);
      }
    } else if(item) {
      this.showSelectedValues = [...item.selectedValues];
    }
  }

  chooseColumnValue(isChecked: boolean, value: string, valueIndex: number): void {
    this.showSelectedColumnValues[valueIndex].isChecked = isChecked;
    if(isChecked) {
      this.selectedUsersOrGroupsInUserFilter[this.selectedUserOrGroupInUserFilter.index].selectedValues
          .push(value);
    } else {
      this.selectedUsersOrGroupsInUserFilter[this.selectedUserOrGroupInUserFilter.index].selectedValues =
          this.selectedUsersOrGroupsInUserFilter[this.selectedUserOrGroupInUserFilter.index].selectedValues
          .filter(tempValue => tempValue !== value);
    }
    this.validator();
  }

  toggleIsInclude(): void {
    this.selectedUserOrGroupInUserFilter.isInclude = !this.selectedUserOrGroupInUserFilter.isInclude;
    this.validator();
  }

  createUserFilter(): void {
    this.submitting = true;
    this.abstractFacade.createUserFilter(this.selectedColumn.id, this.userFilterName,
        [...this.selectedUsersOrGroupsInUserFilter]);
  }

  editUserFilter(): void {
    this.submitting = true;
    this.abstractFacade.updateUserFilter(this.userFilterDetails.userFilterId, this.userFilterName,
        this.userFilterDetails.columnId, [...this.selectedUsersOrGroupsInUserFilter]);
  }

  onScroll($event): void {
    if($event.target.offsetHeight + $event.target.scrollTop >= $event.target.scrollHeight * 0.7
        && !this.requestedForNewData &&
        Number.isInteger(Number((this.selectedColumnValues.length + this.counterAppendData) / this.maxDataAppend))) {
      this.counterAppendData++;
      this.requestedForNewData = true;
      const paginationValues = {
        rowNumber: this.rowNumber,
        searchKey: this.searchValueKey,
        needNewQuery: false,
        values: [
          {
            dataSourceId: this.dataSourceId,
            columnId: this.selectedColumn.id,
            value: this.showSelectedColumnValues[this.showSelectedColumnValues.length - 1].value,
          },
        ],
      };
      this.abstractFacade.loadColumnValues(this.selectedColumn.id, [], paginationValues, true);
    }
  }

  updateSelectedValues(selectedUserOrGroupInUserFilter: Partial<UserInUserFilter>,
                       selectedColumnValues: string[]): void {
    this.showSelectedColumnValues = [];
    if (selectedUserOrGroupInUserFilter) {
      selectedUserOrGroupInUserFilter.selectedValues.forEach((value) => {
        this.showSelectedColumnValues.push({value, isChecked: true});
      });
    }
    selectedColumnValues.forEach(columnValue => {
      const foundedIndex = this.showSelectedColumnValues.findIndex(tempValue => tempValue.value === columnValue);
      if(foundedIndex < 0) {
        this.showSelectedColumnValues.push({value: columnValue, isChecked: false});
      }
    });
  }

  validator(): void {
    this.isValidated = this.userFilterName !== '' && this.userFilterName !== undefined &&
        this.selectedColumn !== undefined && this.isUsersHasValues();
  }

  isUsersHasValues(): boolean {
    let validated = true;
    this.selectedUsersOrGroupsInUserFilter.forEach(tempUser => {
      validated = validated && tempUser.selectedValues.length > 0;
    });
    return validated;
  }

  searchInUsers(searchKey: string): void {
    if (searchKey !== '') {
      this.showSelectedUsersOrGroupsInUserFilter = [];
      this.selectedUsersOrGroupsInUserFilter.forEach(tempUser => {
        if(tempUser.recipientName.toLowerCase().includes(searchKey.toLowerCase())) {
          this.showSelectedUsersOrGroupsInUserFilter.push(tempUser);
        }
      });
    } else {
      this.showSelectedUsersOrGroupsInUserFilter = [...this.selectedUsersOrGroupsInUserFilter];
    }
  }

  searchColumnValue(searchKey: string): void {
    if(this.actionType !== 'View') {
      this.counterAppendData = 0;
      const paginationValues = {
        rowNumber: this.rowNumber,
        searchKey,
        needNewQuery: true,
      };
      this.abstractFacade.loadColumnValues(this.selectedColumn.id, [], paginationValues);
    } else {
      this.showSelectedValues = [];
      this.selectedUserOrGroupInUserFilter.selectedValues.forEach(value => {
        if(value.toLowerCase().includes(searchKey.toLowerCase())) {
          this.showSelectedValues.push(value);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.abstractFacade.resetUserFilterDetails();
    this.abstractFacade.resetSelectedUsersOrGroupsInUserFilter();
    this.abstractFacade.resetSelectedColumnValues();
  }
}
